import type {Thread} from '@github-ui/diff-lines/conversation-types'
import {type MouseEvent, useCallback} from 'react'

import {useNavigateToComment, usePullRequestBasePath} from '../stores/layout-selectors'
import {addUrlToHistoryStack} from '@github-ui/history'
import {useNavigate} from '@github-ui/use-navigate'
import {isThreadOutdated} from '../helpers/thread-navigation'

export const useNavigateToThreadComment = (
  thread: Thread,
  onNavigateToDiffComment: () => void,
  commentId?: number | null,
) => {
  const navigateToSelectedComment = useNavigateToComment()
  const basePath = usePullRequestBasePath()
  const navigate = useNavigate()

  const isOutdated = isThreadOutdated(thread)

  return useCallback(
    (event?: MouseEvent<HTMLElement>) => {
      if (!commentId) return

      // The callback is wired to anchor elements that also carry an `href`. Prevent the browser's
      // default navigation for plain left-clicks so we don't double-navigate, while still letting
      // modifier/middle clicks fall through to the native href (e.g. open-in-new-tab).
      // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic -- Handle locally unless user is opening in a new tab/window
      if (event && (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0)) {
        return
      }
      event?.preventDefault()

      if (isOutdated && thread.originalDiffPathUri) {
        // eslint-disable-next-line no-restricted-syntax
        const url = new URL(thread.originalDiffPathUri)
        url.searchParams.set('new_files_changed', 'true')

        window.location.href = url.toString()
      } else if (navigateToSelectedComment) {
        navigateToSelectedComment(commentId)
        const currentUrl = window.location.pathname + window.location.search
        addUrlToHistoryStack(`${currentUrl}#r${commentId}`)
      } else {
        navigate(`${basePath}/changes#r${commentId}`)
      }
      window.requestAnimationFrame(() => {
        onNavigateToDiffComment()
      })
    },
    [
      commentId,
      isOutdated,
      thread.originalDiffPathUri,
      navigateToSelectedComment,
      navigate,
      basePath,
      onNavigateToDiffComment,
    ],
  )
}
